import { useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  ShoppingBag,
  Megaphone,
  Cpu,
  Coins,
  Building2,
  GraduationCap,
  Video,
  Landmark,
  BarChart3,
  TrendingUp,
  FileText,
  MessageSquare,
  Calendar,
  Leaf,
  Globe,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AnimatedSection, FadeIn, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO, { breadcrumbs } from "@/components/SEO";

// Case studies mapped for inclusion on industry detail pages
const allCaseStudies = [
  {
    id: "concrete-curing-automation",
    title: "AI Vision Concrete Curing Automation",
    client: "Leading Infrastructure Firm",
    description: "Deployed a WhatsApp VLM agent across 100+ site groups to automate concrete curing verification, delivering instant in-chat Pass/Fail feedback and replacing manual HQ Excel logging.",
    tags: ["Computer Vision", "VLM", "Construction", "Custom WhatsApp API", "Quality Assurance"],
    metric: "100+ WhatsApp Groups Automated",
  },
  {
    id: "equipment-ordering-app",
    title: "E-Commerce Ordering Platform for Site Equipment",
    client: "Leading Construction Group",
    description: "Replaced manual phone-based ordering workflows with a robust mobile catalog, reducing delivery errors and integrating field requisitions.",
    tags: ["E-Commerce", "Procurement", "WhatsApp API", "Workflow Automation"],
    metric: "Zero Wrong Equipment Deliveries",
  },
  {
    id: "sovereign-mining-detection",
    title: "Sovereign Mining Detection & ANPR System",
    client: "State Mining Departments",
    description: "Deployed a fully offline dual-camera barricade monitoring system utilizing YOLOv4-Tiny, DeepSORT, and PaddleOCR to interdict illegal mineral transit.",
    tags: ["Edge AI", "Computer Vision", "ANPR", "Sovereign Infrastructure"],
    metric: "98% Illegal Transit Interdiction",
  },
  {
    id: "minemitra-chatbot",
    title: "MineMitra: Cosine Similarity Mining Q&A Bot",
    client: "Sovereign Regulatory Authorities",
    description: "Developed a fully local compliance assistant matching natural language queries to regulation databases using text embedding cosine similarity.",
    tags: ["Local RAG", "Embedding Search", "Compliance Bot", "Sovereign AI"],
    metric: "95% Direct Compliance Query Match",
  },
  {
    id: "face-recognition-attendance",
    title: "Biometric Face Recognition & Presence Monitoring",
    client: "Industrial Checkpoints",
    description: "Implemented an on-premise local attendance tracking and cabin presence monitoring system using RetinaNet and ArcFace embedding matching.",
    tags: ["Biometrics", "Face Detection", "ArcFace", "Edge AI"],
    metric: "99.7% Biometric Verification Accuracy",
  },
  {
    id: "onprem-document-ai",
    title: "Sovereign Document AI & Fraud Detection API",
    client: "Leading National Retail Bank",
    description: "Built a fully on-premise Document Processing API with pixel-level tampering checks, KYC validation, and deep learning OCR.",
    tags: ["On-Premise", "Computer Vision", "Fraud Analysis", "Fintech"],
    metric: "99.9% Document Processing Accuracy",
  },
  {
    id: "compliance-qa-platform",
    title: "Conversational QA & Compliance Audit Platform",
    client: "Global Wealth Management Firm",
    description: "Developed a secure compliance QA tool allowing instant queries over SEC regulations and policies with full citation attribution.",
    tags: ["RAG Engine", "Fintech", "Compliance", "Semantic Search"],
    metric: "90% Faster Compliance Auditing",
  },
  {
    id: "secure-lms-drm",
    title: "Securing & Scaling Enterprise Learning",
    client: "Global Media & Education Conglomerate",
    description: "Built an enterprise-grade LMS featuring secure DRM content protection, AI-driven assessment generation, mass marketing pipelines, and learner dashboards.",
    tags: ["LMS", "DRM", "Generative AI", "Analytics"],
    metric: "100% Security & 80% Faster Setup",
  },
  {
    id: "fmcg-sop-digitization",
    title: "Digitizing S&OP for a Leading FMCG Giant",
    client: "Leading FMCG Company in North America",
    description: "Replaced manual planning with a scalable, data-driven S&OP platform featuring ML-powered demand forecasting.",
    tags: ["S&OP", "FMCG", "Machine Learning"],
    metric: "End-to-End S&OP Transformation",
  },
  {
    id: "sales-operations-digitization",
    title: "Sales & Operations Digitization",
    client: "Global Smartphone Brand",
    description: "Transforming retail strategy with artificial intelligence and Sales Operations Management through centralized platforms.",
    tags: ["S&OP", "Digital Transformation", "Analytics"],
    metric: "Eliminated Manual Reconciliation",
  },
  {
    id: "ai-invoicing-engine",
    title: "Leveraging cognitive document AI for financial decision-making",
    client: "Global Enterprise",
    description: "Data efficiency solution automating financial workflows and handling massive invoice volumes for a global bottler.",
    tags: ["IDP", "Computer Vision", "Finance"],
    metric: "Eliminated Invoice Backlog",
  },
  {
    id: "whatsapp-ai-pitch-bot",
    title: "WhatsApp AI Pitch Bot for Regional Founders",
    client: "Startup Ecosystem",
    description: "AI-powered WhatsApp bot enabling regional founders to create professional pitch decks, breaking language barriers.",
    tags: ["Generative AI", "WhatsApp API", "Multilingual"],
    metric: "Massive Founder Onboarding",
  },
  {
    id: "event-brief-automation",
    title: "Event Brief Automation",
    client: "Events Management Company",
    description: "Forging a path for ethical AI integration and strategic document brief automation in enterprise workflows.",
    tags: ["NLP", "Document AI", "Workflow"],
    metric: "70% Faster Brief Generation",
  },
  {
    id: "esg-reporting-automation",
    title: "ESG Reporting Automation for Compliance",
    client: "Enterprise Corporation",
    description: "AI platform for automated ESG report generation, solving complex reporting requirements with fragmented data.",
    tags: ["ESG", "Document AI", "Compliance"],
    metric: "70% Faster Report Generation",
  },
  {
    id: "ai-bilingual-chatbot",
    title: "AI Bilingual Chatbot for Multilingual Support",
    client: "Customer Service Platform",
    description: "AI chatbot enabling multilingual customer support with English and Hindi language capabilities.",
    tags: ["NLP", "Chatbot", "Multilingual"],
    metric: "60% Faster Query Resolution",
  },
];

const industriesData = {
  "fast-moving-consumer-goods": {
    title: "CPG",
    fullName: "Fast Moving Consumer Goods",
    subtitle: "Digitizing S&OP for FMCG Leaders",
    slogan: "VANCO replaces manual planning with a scalable, ML-driven S&OP platform built for high-volume consumer goods.",
    bgImage: "/industries-images/fmcg.jpg",
    icon: ShoppingBag,
    capabilities: [
      { num: 1, title: "Consolidated Supply Chain Data", desc: "Automate the consolidation of actuals, distributor forecasts, and warehouse stock levels to establish a single source of truth." },
      { num: 2, title: "Multi-Level Sales Visibility", desc: "Track primary, secondary, and tertiary stock movements to capture real-time channel velocity." },
      { num: 3, title: "ML-Powered Demand Prediction", desc: "Deploy advanced machine learning models to forecast demand accurately across all distribution layers." },
      { num: 4, title: "Governance & Audit Trails", desc: "Establish centralized, real-time S&OP dashboards with strict access control and reliable audit histories." },
      { num: 5, title: "Scenario Planning & Promotions", desc: "Model demand shifts, promotions impact, and raw material supply changes to optimize inventory decisions." }
    ],
    metrics: [
      { val: "98%", label: "SKU Forecasting Accuracy" },
      { val: "70%", label: "Inventory Excess Reduction" },
      { val: "100%", label: "Auto-Replenishment Triggers" },
      { val: "24/7", label: "Real-time Demand Tracking" }
    ],
    relatedCaseStudies: ["fmcg-sop-digitization", "sales-operations-digitization", "ai-invoicing-engine"]
  },
  "marketing": {
    title: "Marketing",
    fullName: "Marketing",
    subtitle: "Digitizing Event Brief Automation",
    slogan: "VANCO replaces manual campaign planning and brief generation with structured, secure AI-powered workflows.",
    bgImage: "/industries-images/marketing.jpg",
    icon: Megaphone,
    capabilities: [
      { num: 1, title: "AI-Powered Brief Generator", desc: "Automate the creation of detailed, structured event marketing briefs using custom LLM templates." },
      { num: 2, title: "Intelligent Document Parsing", desc: "Extract key campaign parameters and audience fields automatically from unstructured raw inputs." },
      { num: 3, title: "Role-Based Workflows", desc: "Streamline stakeholder reviews and approval handoffs with automatic system-guided paths." },
      { num: 4, title: "Export-Ready Reporting", desc: "Instantly compile brief data into professional, client-ready formats with one-click export functions." },
      { num: 5, title: "Centralized Brief Management", desc: "Replace manual folder-based documents with a secure, centralized dashboard for tracking all active briefings." }
    ],
    metrics: [
      { val: "70%", label: "Brief Approval Time Saved" },
      { val: "4x", label: "Discoverability Increase" },
      { val: "99%", label: "Speech-to-Text Precision" },
      { val: "NLP", label: "Auto-Metadata Extraction" }
    ],
    relatedCaseStudies: ["event-brief-automation", "whatsapp-ai-pitch-bot", "ai-bilingual-chatbot"]
  },
  "consumer-electronics": {
    title: "Consumer Electronics",
    fullName: "Consumer Electronics",
    subtitle: "Digitizing S&OP for Consumer Electronics",
    slogan: "VANCO aligns sales plans, inventory buffers, and production schedules through automated Sales and Operations Planning (S&OP).",
    bgImage: "/industries-images/electronics.jpg",
    icon: Cpu,
    capabilities: [
      { num: 1, title: "Centralized S&OP Platform", desc: "Transition from fragmented spreadsheets to a unified web platform for end-to-end sales and operations planning." },
      { num: 2, title: "Automated Data Consolidation", desc: "Instantly aggregate actuals, demand forecasts, and regional stock levels to eliminate manual reconciliation." },
      { num: 3, title: "Single Source Dashboards", desc: "Gain end-to-end visibility into global distributor channels and sales cycles with complete audit trails." },
      { num: 4, title: "AI-Ready Data Foundation", desc: "Establish structured data pipelines to prepare operations for advanced machine learning demand forecasting." },
      { num: 5, title: "Real-Time Scenario Analysis", desc: "Simulate capacity constraints, tariff shifts, and promotional impacts to make confident planning decisions." }
    ],
    metrics: [
      { val: "98%", label: "Consensus Plan Accuracy" },
      { val: "30%", label: "Inventory Holding Reduction" },
      { val: "2x", label: "Faster Planning Cycles" },
      { val: "Unified", label: "S&OP Workflows" }
    ],
    relatedCaseStudies: ["sales-operations-digitization", "fmcg-sop-digitization", "ai-invoicing-engine"]
  },
  "fintech": {
    title: "Fintech",
    fullName: "Fintech",
    subtitle: "Transforming Fintech with enterprise AI",
    slogan: "VANCO automates enterprise financial workflows and invoice auditing at scale.",
    bgImage: "/industries-images/fintech.jpg",
    icon: Coins,
    capabilities: [
      { num: 1, title: "Invoice Reconciliation", desc: "Intelligent document processing and vision models for financial workflows." },
      { num: 2, title: "Fraud Detection", desc: "Millisecond-scale anomaly flagging across active transaction networks." },
      { num: 3, title: "Credit Decisioning", desc: "Predictive risk modelling to deliver instant, secure approvals." },
      { num: 4, title: "Regulatory Scanning", desc: "Automated compliance scanning of contracts and financial documents." },
      { num: 5, title: "Expense Management", desc: "ML-based extraction and tagging of employee expense claims." }
    ],
    metrics: [
      { val: "99.9%", label: "Invoice Auditing Automation" },
      { val: "15x", label: "Faster Risk Approvals" },
      { val: "<10ms", label: "Fraud Detection Latency" },
      { val: "SOC2", label: "Compliance Shield" }
    ],
    relatedCaseStudies: ["onprem-document-ai", "compliance-qa-platform", "ai-invoicing-engine", "esg-reporting-automation", "sales-operations-digitization"]
  },
  "construction": {
    title: "Construction",
    fullName: "Construction",
    subtitle: "Transforming Construction with enterprise AI",
    slogan: "VANCO deploys computer vision and predictive models on high-value projects.",
    bgImage: "/industries-images/construction.jpg",
    icon: Building2,
    capabilities: [
      { num: 1, title: "Concrete Curing VLM Inspection", desc: "Automated WhatsApp VLM inspection of concrete wetness, hessian mats, and ponding compliance." },
      { num: 2, title: "PPE Vision Auditing", desc: "Computer vision models to flag safety compliance on active sites." },
      { num: 3, title: "Delay Risk Prediction", desc: "Weather and supply buffer forecasting to optimize timelines." },
      { num: 4, title: "Resource Allocation", desc: "Scheduling optimization algorithms to maximize active duty cycle time." },
      { num: 5, title: "Equipment Telematics", desc: "Predictive maintenance scheduling for high-cost rental fleets." }
    ],
    metrics: [
      { val: "100%", label: "Manual Log Elimination" },
      { val: "90%", label: "PPE Safety Detection" },
      { val: "30%", label: "Fleet Utilization Gain" },
      { val: "VLM AI", label: "Real-time Site Audits" }
    ],
    relatedCaseStudies: ["concrete-curing-automation", "equipment-ordering-app", "face-recognition-attendance", "sovereign-mining-detection", "minemitra-chatbot", "esg-reporting-automation", "sales-operations-digitization", "event-brief-automation"]
  },
  "edtech": {
    title: "Edtech",
    fullName: "Edtech",
    subtitle: "Transforming Edtech with enterprise AI",
    slogan: "VANCO scales customized learning paths and multilingual support engines.",
    bgImage: "/industries-images/edtech.jpg",
    icon: GraduationCap,
    capabilities: [
      { num: 1, title: "Adaptive Study Paths", desc: "Dynamic recommendation engines that adjust lesson difficulty" },
      { num: 2, title: "Multilingual Tutoring", desc: "Curriculum Q&A support bots answering in regional dialects." },
      { num: 3, title: "AI Grading Assistants", desc: "Structured pre-grading and feedback helpers for teachers." },
      { num: 4, title: "Engagement Prediction", desc: "Early detection models identifying students at risk of drop-out." },
      { num: 5, title: "Content Structuring", desc: "Automated summarization and index tagging of educational videos." }
    ],
    metrics: [
      { val: "92%", label: "Learning Engagement Rate" },
      { val: "60%", label: "Reduction in Grading Workload" },
      { val: "12+", label: "Supported Dialects" },
      { val: "COPPA", label: "Privacy Protection" }
    ],
    relatedCaseStudies: ["secure-lms-drm", "ai-bilingual-chatbot", "whatsapp-ai-pitch-bot"]
  },
  "digital-media": {
    title: "Media",
    fullName: "Digital Media",
    subtitle: "Transforming Media with enterprise AI",
    slogan: "VANCO accelerates creative campaign briefing and media tag auto-indexing.",
    icon: Video,
    capabilities: [
      { num: 1, title: "Campaign Brief Generation", desc: "Custom LLM templates producing approved media briefs in minutes." },
      { num: 2, title: "Speech-to-Text Tagging", desc: "Media processing models auto-extracting keywords." },
      { num: 3, title: "Content Personalization", desc: "Contextual recommendations to maximize viewer duration." },
      { num: 4, title: "Metadata Auto-indexing", desc: "Computer vision identifying visual assets for easier search and retrieval." },
      { num: 5, title: "Brief Audits & Approvals", desc: "Natural language checks to ensure legal campaign compliance." }
    ],
    metrics: [
      { val: "70%", label: "Brief Approval Time Saved" },
      { val: "4x", label: "Discoverability Increase" },
      { val: "99%", label: "Speech-to-Text Precision" },
      { val: "NLP", label: "Auto-Metadata Extraction" }
    ],
    relatedCaseStudies: ["event-brief-automation", "whatsapp-ai-pitch-bot", "ai-bilingual-chatbot"]
  },
  "government": {
    title: "Government",
    fullName: "Government",
    subtitle: "Transforming Government with enterprise AI",
    slogan: "VANCO modernizes public administration with secure, sovereign document AI.",
    icon: Landmark,
    capabilities: [
      { num: 1, title: "Document Processing IDP", desc: "Instant validation of forms, registration papers, and applications." },
      { num: 2, title: "Localized Support Portals", desc: "Conversational bots helping citizens find correct services." },
      { num: 3, title: "Sovereign AI Deployment", desc: "Private-cloud hosted custom LLMs maintaining data governance." },
      { num: 4, title: "Benefits Allocation AI", desc: "Anonymized screening models optimizing resource disbursement." },
      { num: 5, title: "Public Health Forecasting", desc: "Predictive models mapping local wellness and service workloads." }
    ],
    metrics: [
      { val: "99%", label: "Document Verification Precision" },
      { val: "15x", label: "Citizen Response Rates" },
      { val: "100%", label: "Sovereign Cloud Hosting" },
      { val: "Federal", label: "Security Compliant" }
    ],
    relatedCaseStudies: ["sovereign-mining-detection", "minemitra-chatbot", "esg-reporting-automation", "ai-bilingual-chatbot", "event-brief-automation"]
  }
};

export default function IndustryDetailPage() {
  const { id } = useParams();
  const industry = industriesData[id];

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 py-32 px-4 animate-fadeIn" data-testid="industry-not-found">
        <div className="text-center max-w-md bg-white p-8 rounded-3xl border border-slate-100 shadow-xl">
          <h2 className="text-3xl font-bold text-slate-800 font-['Manrope'] mb-4">Industry Not Found</h2>
          <p className="text-slate-600 mb-6">The industry you are looking for does not exist or has been moved.</p>
          <Link to="/">
            <Button className="bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full px-6">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const matchedStudies = industry.relatedCaseStudies
    .map(studyId => allCaseStudies.find(study => study.id === studyId))
    .filter(Boolean)
    .slice(0, 3);
  const IndustryIcon = industry.icon;

  const industryDescription = industry
    ? `${(industry.slogan || "").replace(/Vanco(\s+AI)?/g, "VANCO")} Explore AI solutions and case studies for ${industry.fullName}.`.slice(0, 160)
    : "";
  return (
    <div key={id} className="overflow-hidden bg-white text-slate-800" data-testid="industry-page" id={`industry-page-${id}`}>
      <SEO
        title={`${industry.fullName} AI Solutions | VANCO`}
        description={industryDescription}
        path={`/industries/${id}`}
        jsonLd={breadcrumbs([
          { name: "Home", path: "/" },
          { name: `${industry.fullName} AI Solutions`, path: `/industries/${id}` },
        ])}
      />
      {/* Hero Section */}
      <section 
        className={`relative pt-36 pb-28 md:py-44 flex items-center overflow-hidden ${
          industry.bgImage 
            ? "bg-slate-950 text-white" 
            : "bg-gradient-to-br from-white via-slate-50 to-blue-50 text-slate-800"
        }`} 
        data-testid="industry-hero"
      >
        {/* Background Image & Dark Layer Overlay for industries with custom bgImage */}
        {industry.bgImage && (
          <>
            <img 
              src={industry.bgImage} 
              alt={industry.fullName} 
              className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none z-0" 
            />
            {/* Dark Layer Overlay */}
            <div className="absolute inset-0 z-0 bg-gradient-to-r from-slate-950/90 via-slate-950/80 to-slate-900/65 backdrop-brightness-75" />
          </>
        )}

        {/* Visual Pill Arches (only for default light background pages without bgImage) */}
        {!industry.bgImage && (
          <div className="absolute bottom-0 right-0 z-0 flex items-end gap-1.5 pointer-events-none select-none">
            {/* Arch 1 (shortest) - Soft Brand Gold */}
            <div className="w-20 md:w-28 lg:w-36 h-36 md:h-52 lg:h-64 bg-[#fcdfa2] rounded-t-full shadow-[-10px_-5px_30px_rgba(15,23,42,0.05)]" />
            {/* Arch 2 (middle) - Muted Brand Blue */}
            <div className="w-20 md:w-28 lg:w-36 h-52 md:h-72 lg:h-96 bg-[#8ea0cc] rounded-t-full shadow-[-15px_-5px_40px_rgba(15,23,42,0.07)]" />
            {/* Arch 3 (tallest) - Deep Brand Blue */}
            <div className="w-24 md:w-32 lg:w-44 h-72 md:h-96 lg:h-[480px] bg-[#243b7f] rounded-t-full shadow-[-20px_-5px_50px_rgba(15,23,42,0.1)]" />
          </div>
        )}

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left Column Content */}
            <div className="text-left relative z-10 max-w-xl">
              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-['Manrope'] mb-6 leading-tight ${
                industry.bgImage ? "text-white drop-shadow-md" : "text-[#0f172a]"
              }`}>
                {industry.subtitle}
              </h1>

              <p className={`text-lg md:text-xl font-semibold leading-relaxed mb-8 font-['Inter'] ${
                industry.bgImage ? "text-slate-200" : "text-[#243b7f]"
              }`}>
                {industry.slogan}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className={`rounded-full px-8 py-6 text-base font-semibold transition-all duration-300 shadow-lg ${
                      industry.bgImage
                        ? "bg-[#f7c550] hover:bg-[#f5b82e] text-slate-950 shadow-amber-500/20 font-bold"
                        : "bg-[#243b7f] hover:bg-[#1e3a8a] text-white shadow-blue-900/20"
                    }`}
                  >
                    Talk to an expert
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Column Spacer */}
            <div className="hidden lg:block h-[350px] pointer-events-none" />

          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-28 bg-[#243b7f]" data-testid="industry-value-props">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">

          <div className="space-y-8">
            {/* Row 1: 2 items */}
            <AnimatedContainer className="grid md:grid-cols-2 gap-8">
              {industry.capabilities.slice(0, 2).map((cap) => (
                <AnimatedItem key={cap.title} className="h-full">
                  <div className="h-full flex flex-col p-8 rounded-3xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <h3 className="text-lg font-bold text-[#f7c550] font-['Manrope'] mb-3">
                      {cap.title}
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed font-['Inter']">
                      {cap.desc}
                    </p>
                  </div>
                </AnimatedItem>
              ))}
            </AnimatedContainer>

            {/* Row 2: 3 items */}
            <AnimatedContainer className="grid md:grid-cols-3 gap-8">
              {industry.capabilities.slice(2, 5).map((cap) => (
                <AnimatedItem key={cap.title} className="h-full">
                  <div className="h-full flex flex-col p-8 rounded-3xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-all duration-300">
                    <h3 className="text-lg font-bold text-[#f7c550] font-['Manrope'] mb-3">
                      {cap.title}
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed font-['Inter']">
                      {cap.desc}
                    </p>
                  </div>
                </AnimatedItem>
              ))}
            </AnimatedContainer>
          </div>

        </div>
      </section>

      {/* Success Stories Section */}
      {matchedStudies.length > 0 && (
        <section className="py-28 bg-slate-50/50 border-t border-b border-slate-100" data-testid="industry-case-studies">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">

            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope']">
                Success stories
              </h2>
            </div>

            <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {matchedStudies.map((study) => (
                <AnimatedItem key={study.id}>
                  <Link to={`/case-studies/${study.id}`} className="group block h-full">
                    <div className="h-full flex flex-col justify-between min-h-[320px] bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm hover:shadow-2xl hover:shadow-slate-200/50 hover:-translate-y-2 transition-all duration-500 overflow-hidden">
                      <div>
                        {/* Case Study Badge */}
                        <div className="inline-block px-3 py-1.5 bg-[#f7c550] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded-md mb-6">
                          Case Study
                        </div>

                        {/* Title */}
                        <h3 className="text-[#0a122c] text-xl font-bold font-['Manrope'] mb-4 leading-snug group-hover:text-[#243b7f] transition-colors">
                          {study.title}
                        </h3>

                        {/* Description */}
                        <p className="text-slate-600 text-sm leading-relaxed font-['Inter'] mb-6">
                          {study.description}
                        </p>
                      </div>

                      {/* Read Now link */}
                      <span className="text-[#243b7f] font-semibold text-sm hover:underline inline-flex items-center gap-1 mt-auto">
                        Read Now
                        <ArrowRight className="w-4 h-4 ml-0.5" />
                      </span>
                    </div>
                  </Link>
                </AnimatedItem>
              ))}
            </AnimatedContainer>

          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-28 bg-[#243b7f]" data-testid="industry-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Manrope'] mb-6">
            Partner with us to transform your operations
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto font-['Inter']">
            Let's discuss how we can deploy custom AI engines to address the specific challenges of the {industry.fullName} sector.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
            >
              Start an Industry Initiative
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

    </div>
  );
}
